import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../api/supabase';
import { ROLE_DEFAULTS, DASHBOARD_WIDGETS } from '../config/permissions';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]         = useState(null);
  const [staffProfile, setStaffProfile] = useState(null);
  const [loading, setLoading]   = useState(true);
  // A password-recovery link logs the user in (it carries a valid session token) but
  // that's not the same as "let them straight into the app". Supabase fires a
  // PASSWORD_RECOVERY auth event for this, but that event comes from the client's own
  // async initialize()/detectSessionInUrl flow, which can already have fired (and been
  // missed) before this component's useEffect subscribes — the client is created at
  // module-import time, well before any React effect runs. Check the URL hash directly
  // too, synchronously at first render, so this doesn't depend on winning that race.
  const [passwordRecovery, setPasswordRecovery] = useState(() => /type=recovery/.test(window.location.hash));

  async function loadProfile(authUser) {
    if (!authUser) { setStaffProfile(null); return; }
    const { data } = await supabase
      .from('staff')
      .select('role, agent_ref_id, tab_permissions, dashboard_widgets, branch_id, branches(name, code)')
      .eq('email', authUser.email)
      .maybeSingle();
    setStaffProfile(data || { role: 'admin', agent_ref_id: null, tab_permissions: null, dashboard_widgets: null, branch_id: null, branches: null });
  }

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      await loadProfile(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);
      setUser(session?.user ?? null);
      await loadProfile(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const login = async (username, password) => {
    const email = username.includes('@') ? username : `${username}@tls.internal`;
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setUser(data.user);
    await loadProfile(data.user);
    return data.user;
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setStaffProfile(null);
  };

  // Called from the "set new password" screen shown while passwordRecovery is true —
  // updateUser works here because the recovery link already left Supabase holding a
  // valid (if temporary) session for this user.
  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    setPasswordRecovery(false);
  };

  const role        = staffProfile?.role || 'admin';
  const agentRefId  = staffProfile?.agent_ref_id || null;
  const isAgent     = role === 'agent';
  const branchId    = staffProfile?.branch_id || null;
  const branchName  = staffProfile?.branches?.name || null;
  const branchCode  = staffProfile?.branches?.code || null;
  const dashboardWidgets = staffProfile?.dashboard_widgets || DASHBOARD_WIDGETS.map(w => w.key);

  const canSee = (tabKey) => {
    if (role === 'sadmin' || role === 'admin') return true;
    const perms = staffProfile?.tab_permissions;
    if (perms) return perms.includes(tabKey);
    return ROLE_DEFAULTS[role]?.includes(tabKey) ?? true;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, role, agentRefId, isAgent, canSee, branchId, branchName, branchCode, dashboardWidgets, passwordRecovery, updatePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
